import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import type { TabInfo } from './sysml-tab-bar.js';
import './sysml-tab-bar.js';

// ---------------------------------------------------------------------------
// <sysml-editor-group> — Single editor group (tab bar + content slot)
//
// Each group has its own tab bar and renders either an editor or diagram.
// The parent <sysml-editor-zone> manages multiple groups in a split layout.
//
// Content is projected via a named slot so the workspace can pass any
// Stencil/Lit component (sysml-editor, sysml-diagram, etc.) as content.
// ---------------------------------------------------------------------------

export type GroupContentType = 'editor' | 'diagram';

@customElement('sysml-editor-group')
export class SysmlEditorGroup extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
      }
      :host(.focused) {
        outline: 1px solid var(--r5-primary);
        outline-offset: -1px;
      }

      .group-header {
        display: flex;
        align-items: center;
        flex-shrink: 0;
      }

      .split-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 32px;
        border: none;
        border-bottom: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
        color: var(--r5-text-muted);
        cursor: pointer;
        flex-shrink: 0;
        padding: 0;
      }
      .split-btn:hover {
        color: var(--r5-text);
        background: color-mix(in srgb, var(--r5-text) 8%, transparent);
      }

      .content {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      /* Slotted content takes full space */
      ::slotted(*) {
        display: block;
        width: 100%;
        height: 100%;
        flex: 1;
        min-height: 0;
      }
    `,
  ];

  @property({ attribute: 'group-id' }) groupId = '';
  @property({ attribute: false }) contentType: GroupContentType = 'editor';
  @property({ type: Array }) tabs: TabInfo[] = [];
  @property({ attribute: 'active-tab-id' }) activeTabId: string | null = null;
  @property({ type: Boolean, attribute: 'show-tabs' }) showTabs = true;
  @property({ type: Boolean }) focused = false;

  override render() {
    return html`
      ${this.showTabs
        ? html`
            <div class="group-header">
              <sysml-tab-bar
                style="flex: 1; min-width: 0;"
                .tabs=${this.tabs}
                active-tab-id=${this.activeTabId ?? ''}
                @tab-select=${this._forward}
                @tab-close=${this._forward}
                @tab-pin=${this._forward}
                @tab-reorder=${this._forward}
              ></sysml-tab-bar>
              <button
                class="split-btn"
                title="Split Editor"
                @click=${this._onSplitRequest}
              >
                <ark-icon name="columns" size="xs"></ark-icon>
              </button>
            </div>
          `
        : nothing}
      <div class="content">
        <slot></slot>
      </div>
    `;
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('focused')) {
      this.classList.toggle('focused', this.focused);
    }
  }

  private _onSplitRequest(): void {
    this.dispatchEvent(
      new CustomEvent('split-request', {
        detail: { groupId: this.groupId },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Forward tab-bar events unchanged */
  private _forward(e: Event): void {
    const ce = e as CustomEvent;
    this.dispatchEvent(
      new CustomEvent(ce.type, {
        detail: { ...ce.detail, groupId: this.groupId },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-editor-group': SysmlEditorGroup;
  }
}
